import { useState, useMemo, useRef, useEffect } from "react";
import { Button, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import DataTablePageQuery from "../../../components/DataTablePageQuery";
import { useTheme } from '../../../hooks/useTheme';
import { updateQuoteRevision } from "../data/quoteRevisionService";
import QuoteRevisionModal from "./quoteRevisionModal";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatDateTime } from '../../../utils/dateUtils';
import Avatar from "../../../components/Avatar";
import { fetchUsers } from "../../users/data/userService";
import { useQuery } from "@tanstack/react-query";
import { useLockState } from "../../../components/userLocks/LockCheck";
import { useSalesforceSyncContext } from "../../../contexts/salesforceSyncContext";
import { fetchQuoteItems } from "../data/quoteItemService";
import SalesforceSyncStatus from "./SalesforceSyncStatus";
import DataLoader from "../../../components/loader/DataLoader";
import { getOpportunityLiveFiles, replaceActiveQuoteInLiveFolder, hasGeneratedQuote } from '../../sharepoint/data/sharepointService';
import QuoteLiveFilesDialog from "./quoteLiveFilesDialog";
import { createAudit } from "../../../features/audit/data/auditService";
import { toast } from "react-toastify";

export default function QuoteRevisions({ quoteId, quoteRevisionId, quoteRevisions, isLoadingQuoteRevisions, quoteRevisionsError, refetchQuoteRevisions, setQuoteRevisionsFilter, quoteRevisionsTotalRecords, isActive, quoteData, sharepointFolder }) {
    const theme = useTheme();
    const { id: opportunityId, quoteId: currentQuoteId } = useParams();
    const navigate = useNavigate();
    const COLUMN_SETTINGS_KEY = "quoteRevisionsTableSettings";
    const user = useSelector((state) => state.authentication?.user);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingRevision, setEditingRevision] = useState(null);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const toggleSettingsModal = () => setSettingsModalOpen(!settingsModalOpen);
    const lockState = useLockState();
    const activeRowRef = useRef(null);
    const [isReadingSharepointFiles, setIsReadingSharepointFiles] = useState(false);
    const [isUpdatingLiveFolder, setIsUpdatingLiveFolder] = useState(false);
    const [isSharepointFilesDialogOpened, setIsSharepointFilesDialogOpened] = useState(false);
    const [ liveFiles, setLiveFiles ] = useState([]);
    const hasGeneratedQuoteRef = useRef(false);

    const salesforceSync = useSalesforceSyncContext();

    const openCreate = () => { setEditingRevision(null); setModalOpen(true); };
    const openEdit = (id) => {
        const row = quoteRevisions.find(r => r.id === id);
        if (!row) return;
        setEditingRevision(row);
        setModalOpen(true);
    };

    const syncToSalesforce = async (quoteRevision) => {
        const quote = { ...quoteData, isActive: quoteData?.isActive ?? false };

        if ( quote.isActive && quoteRevision.isMain) {
            const quoteItems = await fetchQuoteItems(quoteRevision.id);
            await salesforceSync.syncToSalesforce({
                quote, 
                quoteRevision, 
                quoteItems,
                executedBy: salesforceSyncExecutionById
            });
        }
    }

    const salesforceSyncExecutionById = "Quote Revisions Table";    

    const activeQuoteRevisionRef = useRef();

    useEffect(() => {
        const main = quoteRevisions?.find(r => r?.isMain);
        if (!main) return; // Main revision not on this page — don't update the ref or trigger a sync

        const prev = activeQuoteRevisionRef.current;
        if (prev && main.id !== prev.id) {
            // The main revision has changed
            syncToSalesforce(main).catch((err) => {
                console.error("Salesforce sync failed after revision change", err);
                toast.error("Salesforce sync failed.");
            });
        }

        activeQuoteRevisionRef.current = main;
    }, [quoteRevisions]);


    const isLocked = useMemo(() => lockState?.isLocked && lockState?.lockedBy.connectionId !== lockState?.currentUser?.connectionId);

    const handleRowClick = (item) => {
        navigate(`/opportunities/${opportunityId}/${currentQuoteId}/${item.id}`);
    };

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }));
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["quoteUsersLookupFilter"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });

    const columns = [
        {
            key: "isMain",
            label: "",
            style: { width: "50px" },
            isDefault: true,
            hideSettings: true,
            render: (value, row) =>
                <div 
                    onClick={(e) => {
                        if (!value) {
                            // Not the currently main revision, so clicking it will make it so
                            openDialogBox(e, row);
                        } else {
                            e.stopPropagation(); // Already the main revision, clicking does nothing, so just stop event bubbling to prevent unwanted navigation
                            toast.info("This is already the main revision.", { autoClose: 2000, hideProgressBar: true })
                        }
                    }} 
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", cursor: "pointer" }}
                >
                    {
                        value ? (
                            <i className={`bi bi-star-fill ${lockState?.isLocked ? "locked" : ""}`} title="Main revision" />
                        ) : (
                                <i
                                    className={`bi bi-star icon-button ${lockState?.isLocked ? "locked" : ""}`}
                                    title="Set as main revision"
                                />
                        )
                    }
                </div>
        },
        { key: "revisionNumber", label: "Revision Number", isDefault: true },
        { key: "name", label: "Name", isDefault: true },
        { key: "notes", label: "Notes", isDefault: true },
        { key: "createdAt", label: "Created", isDefault: true, render: (_, row) => <span>{formatDateTime(row.createdAt)}</span> },
        { key: "updatedAt", label: "Updated", render: (_, row) => <span>{formatDateTime(row.updatedAt)}</span> },
        {
            key: "createdBy", label: "Created By", isDefault: true, render: (_, row) => {
                const user = (usersLookup || []).find(f => f.userGuid === row.createdBy);
                return (!user ? "-" :
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Avatar email={user.email} uid={user.email} />
                        <span style={{ marginLeft: "10px" }}>{user.firstName + " " + user.lastName}</span>
                    </div>
                );
            }
        },
        {
            key: "updatedBy", label: "Updated By", render: (_, row) => {
                const user = (usersLookup || []).find(f => f.userGuid === row.updatedBy);
                return (!user ? "-" :
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Avatar email={user.email} uid={user.email} />
                        <span style={{ marginLeft: "10px" }}>{user.firstName + " " + user.lastName}</span>
                    </div>
                );
            }
        },
        {
            key: "actions",
            label: "",
            isDefault: true,
            system: true,
            hideSettings: true,
            isSortable: false,
            style: { width: "100px" },
            className: "text-end",
            render: (value, row) => {
                const someoneIsLockingThisQuoteRevision = isLocked && quoteRevisionId === row.id.toString();
                const disableClick = (row.isLocked || someoneIsLockingThisQuoteRevision);

                return (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!disableClick) {
                                openEdit(row.id);
                            }
                        }}
                        title={disableClick ? (row.isLocked ? "This revision has been finalised." : "Another user is currently editing this revision.") : "Edit Revision"}
                    >
                        <Button
                            className={disableClick ? "locked" : ""}
                            size="sm"
                        >
                            <i className="bi bi-pencil"></i>
                        </Button>
                    </div>
                );
            }
        }
    ];

    const defaultVisibleColumns = columns.reduce((acc, col) => {
        acc[col.key] = !!col.isDefault;
        return acc;
    }, {});

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const stored = localStorage.getItem(COLUMN_SETTINGS_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const merged = { ...defaultVisibleColumns, ...parsed }; // ensures new columns added
                return merged;
            } catch {
                return defaultVisibleColumns;
            }
        }
        return defaultVisibleColumns;
    });

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        if (filterId === "pageNumber") {
            setQuoteRevisionsFilter(prev => ({ ...prev, [filterId]: value }));
        }
        else if (filterId === "onlyShowActiveItems") {
            if (typeof value === 'object')
                setQuoteRevisionsFilter(prev => ({ ...prev, [filterId]: value.value }));
            else
                setQuoteRevisionsFilter(prev => ({ ...prev, [filterId]: value }));
        }
        else if (filterId === "searchTerm") {
            if (value === '') {
                setQuoteRevisionsFilter(({ searchOptions, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                const searchableFields = Object.entries(visibleColumns).filter(([_, value]) => value === true).map(([key]) => key);
                let searchOptions = { searchableFields: searchableFields, searchTerm: value, fieldSearchTerms: [] }
                setQuoteRevisionsFilter(prev => ({ ...prev, searchOptions: searchOptions, pageNumber: 1 }));
            }
        }
        else {
            setQuoteRevisionsFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
        }
    }

    const closeDialogBox = () => setIsSharepointFilesDialogOpened(false);
    
    const openDialogBox = async (e, row) => {
        e.stopPropagation();
        
        activeRowRef.current = row;

        if (!quoteData.isActive) {
            const resultOK = await updateQuoteRevisionStatus();
            console.warn(`Module: Change main revision. Warning: The LIVE folder has not been updated because the quote ${quoteData.id} is not active.`);
            if (resultOK) {
                const successMessage = `Module: Change main revision. Result: Revision ${activeRowRef.current.revisionNumber} marked as active. The LIVE folder has not been updated because the quote ${quoteData.id} is not active.`
                console.log("✅ ", successMessage);
                await createAudit({
                    action: "Changed main revision",
                    result: "Successful",
                    source: `quote.${quoteData.id}`,
                    payload: JSON.stringify({ message: successMessage }),
                });
            }            
            
            return;
        }

        const isValidSharepointFolder = sharepointFolder && sharepointFolder !== "null"
        const validInputFields = isValidSharepointFolder && quoteId != null && activeRowRef.current.revisionNumber != null;        
        if (validInputFields) setIsReadingSharepointFiles(true);

        let hasQuote = false;
        if (validInputFields && row.isLocked) {
            const { message: quoteExists, responseOk } = await hasGeneratedQuote(sharepointFolder, quoteId, activeRowRef.current.revisionNumber);
            hasQuote = responseOk && quoteExists;
        }
        hasGeneratedQuoteRef.current = hasQuote;

        if (!hasQuote) {
            const resultOK = await updateQuoteRevisionStatus();
            if (row.isLocked) {
                toast.warn("The generated document could not be found. The operation will continue but no changes have been made to the LIVE folder")
                console.warn(`Module: Change main revision. Warning: No generated quote found in Sharepoint for quote ${quoteData.id}, revision ${activeRowRef.current.revisionNumber}.`);
            }
            if (resultOK) {
                const successMessage = `Module: Change main revision. Result: Revision ${activeRowRef.current.revisionNumber} marked as active. The LIVE folder has not been updated because no generated quote was found in Sharepoint for revision ${activeRowRef.current.revisionNumber}.`
                console.log("✅ ",successMessage);
                await createAudit({
                    action: "Changed main revision",
                    result: "Successful",
                    source: `quote.${quoteData.id}`,
                    payload: JSON.stringify({ message: successMessage }),
                });
            }

            setIsReadingSharepointFiles(false);
            return;
        }

        setIsReadingSharepointFiles(true);

        const { message: liveMessage, responseOk: liveResponseOk } =  await getOpportunityLiveFiles(sharepointFolder, quoteId, activeRowRef.current.revisionNumber, "", false, null, false);
        if (!liveResponseOk) {
            toast.error(liveMessage);
            console.error(`Error retrieving live files - ${liveMessage}`);            
            setIsReadingSharepointFiles(false);
            return;
        }

        setLiveFiles(liveMessage);

        setIsReadingSharepointFiles(false);
        setIsSharepointFilesDialogOpened(true)
    }

    const updateQuoteRevisionStatus = async () => {
        try {
            const payload = structuredClone(activeRowRef.current);
            payload.IsMain = true;
            payload.UpdatedBy = user?.email || "unknown";

            if (quoteRevisions && quoteRevisions.length > 1) {
                await updateQuoteRevision(payload);
                await refetchQuoteRevisions();
            }
            return true;

        } catch (err) {
            console.error("Set main revision failed", err);
            alert("Failed to set main revision.");
            return false;
        }
    };

    const updateRevisionAndLiveFolder = async (updateLiveFolder) => {
        if (!updateLiveFolder)
        {
            const resultOK = await updateQuoteRevisionStatus();
            if (resultOK) {
                const action = hasGeneratedQuoteRef.current ? "The LIVE folder has not been updated by user choice" : "The LIVE folder has not been updated because the generated document could not be found";
                const successMessage = `Module: Change main revision. Result: Revision ${activeRowRef.current.revisionNumber} marked as active. ${action}.`
                console.log("✅ ",successMessage);
                await createAudit({
                    action: "Changed main revision",
                    result: "Successful",
                    source: `quote.${quoteData.id}`,
                    payload: JSON.stringify({ message: successMessage }),
                });
            }
            setIsSharepointFilesDialogOpened(false);
            return;
        }
            
        const mainRevisionNumber = activeRowRef.current.revisionNumber;
        if (updateLiveFolder && sharepointFolder && quoteId && mainRevisionNumber) {
            setIsUpdatingLiveFolder(true);
            const result = await replaceActiveQuoteInLiveFolder(sharepointFolder, quoteId, mainRevisionNumber);        
            if (result.responseOk) {
                const resultOK = await updateQuoteRevisionStatus();
                if (resultOK) {
                    const successMessage = `Module: Change main revision. Result: Revision ${activeRowRef.current.revisionNumber} marked as active. The LIVE folder has been updated by user choice.`
                    console.log("✅ ",successMessage);
                    await createAudit({
                        action: "Changed main revision",
                        result: "Successful",
                        source: `quote.${quoteData.id}`,
                        payload: JSON.stringify({ message: successMessage }),
                    });
                }
            } else {
                toast.error(`Revision ${activeRowRef.current.revisionNumber} not marked as active. ${result.message ? `Error: ${result.message}` : ""  }`);
            }
            setIsUpdatingLiveFolder(false);
        }
        
        setIsSharepointFilesDialogOpened(false);
    }

    return (
        <>
            <Card>
                <CardBody>
                    <DataTablePageQuery
                        data={quoteRevisions}
                        columns={columns.filter(col => visibleColumns[col.key])}
                        isLoading={isLoadingQuoteRevisions}
                        error={quoteRevisionsError}
                        onRowClick={handleRowClick}
                        rowStyle={(item) => (parseInt(quoteRevisionId) === parseInt(item.id)) ? { cursor: "text" } : { cursor: "pointer" }}
                        includeCompleted={true}
                        totalRecords={quoteRevisionsTotalRecords}
                        sortConfiguration={{ key: 'revisionNumber', direction: 'descending' }}
                        searchableFields={["name", "revisionNumber", "notes", "createdAt", "updatedAt"]}
                        onFilterChange={changeFilter}
                        exportToCsv={false}
                        filters={[
                            {
                                id: "createdAt",
                                label: "Created",
                                type: "daterange",
                                advanced: true
                            },
                            {
                                id: "updatedAt",
                                label: "Updated",
                                type: "daterange",
                                advanced: true
                            }
                        ]}
                        actions={[
                            <Button key="create" onClick={openCreate}>Create New Revision</Button>,
                            <Button outline color={theme === "dark-mode" ? "dark" : "light"} key="settings" onClick={toggleSettingsModal}>
                                <i className="bi bi-gear fs-5"></i>
                            </Button>
                        ]}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={settingsModalOpen} toggle={toggleSettingsModal} scrollable centered backdrop="static">
                <ModalHeader toggle={toggleSettingsModal}>Table Settings</ModalHeader>
                <ModalBody>
                    {columns.filter(f => !f.hideSettings).map((col) => (
                        <FormGroup switch key={col.key}>
                            <Input
                                type="switch"
                                role="switch"
                                id={`switch-${col.key}`}
                                checked={visibleColumns[col.key]}
                                disabled={col.system} // 🔒 disable if it's a system column
                                onChange={(e) => {
                                    const updated = {
                                        ...visibleColumns,
                                        [col.key]: e.target.checked
                                    };
                                    setVisibleColumns(updated);
                                    localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(updated));
                                }}
                            />
                            <Label check htmlFor={`switch-${col.key}`}>
                                {col.label || "Add To Basket"} {col.system && <small className="text-muted">(Required)</small>}
                            </Label>
                        </FormGroup>
                    ))}
                    <br />
                    <Button outline color="danger" size="sm" onClick={() => {
                        setVisibleColumns(defaultVisibleColumns);
                        localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(defaultVisibleColumns));
                    }}>
                        Reset to Defaults
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleSettingsModal}>Close</Button>
                </ModalFooter>
            </Modal>

            <QuoteRevisionModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                quoteId={Number(quoteId)}
                opportunityId={opportunityId}
                revision={editingRevision}
                showIsMainToggle={false}
                onSaved={refetchQuoteRevisions}
                isActive={isActive}
            />

            <SalesforceSyncStatus executedBy={salesforceSyncExecutionById} />

             <QuoteLiveFilesDialog
                isDialogBoxOpened={isSharepointFilesDialogOpened}
                liveFiles={liveFiles}
                closeDialogBox={closeDialogBox}
                skipButtonAction={() => updateRevisionAndLiveFolder(false)}
                updateLiveButtonAction={() => updateRevisionAndLiveFolder(true)}
            />
            <Modal isOpen={isUpdatingLiveFolder} centered size="lg">
                <DataLoader showLoadingTitle={false} message="Updating LIVE folder..." />
            </Modal>            
            <Modal isOpen={isReadingSharepointFiles} centered size="lg">
                <DataLoader showLoadingTitle={false} message="Reading files from Sharepoint..." />
            </Modal>
        </>
    );
};